import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ContextService, MetaService, SchoolsService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { loadMe } from '@store/actions/profile.actions';
import { CLibraryFilterData } from '@store/models';
import { getFeaturingsByKey } from '@store/reducers/featuring.reducer';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { getMe } from '@store/reducers/profile.reducer';
import { AnyType, IContentPane, User } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter, first, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-school-main',
  templateUrl: './school-main.component.html',
  styleUrls: ['./school-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchoolMainComponent extends ChangableComponent implements OnInit {
  // DI
  public deviceService = inject(DeviceDetectorService);
  private store = inject(Store);
  private schoolService = inject(SchoolsService);
  private contextService = inject(ContextService);
  private metaService = inject(MetaService);

  public panesList: IContentPane[] = [];
  public displayList = true;
  public contentFilter: CLibraryFilterData;
  public loading = false;
  public stabHidden = true;

  public debug$ = this.contextService.debug$;
  public debugInfo: AnyType;
  private _me: User;
  private remainUnloaded = 0;
  private paneRequestsCount = 0;

  public ngOnInit() {
    this.setupMeta();
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.displayList = false;
          this.schoolService.setReloadSchoolsState();
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });

    this.store
      .select(getLibraryRouteData)
      .pipe(
        filter((v) => !!v),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (data) => {
          this.contentFilter = data;
          this.loadContentBlocks();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.schoolService.reloadSchools$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (state) => {
        this.displayList = !state;
        this.markForCheck();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.displayList = false;
      },
    });
  }

  public trackByFn(index: number, item: IContentPane): number {
    return item.order;
  }

  private loadContentBlocks() {
    this.loading = true;
    this.detectChanges();
    this.paneRequestsCount++;
    this.store
      .select(getFeaturingsByKey, { contentFilter: this.contentFilter })
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          if (res) {
            this.loading = false;
            this.panesList = res.data;
            this.paneRequestsCount = 0;
            this.debugInfo = res.meta.context?.debugInfo;
            this.stabHidden = this.panesList.length > 0;
            this.remainUnloaded = res.data.length;
            this.detectChanges();
          } else {
            if (this.paneRequestsCount < 10) {
              this.setTimeout(() => {
                this.loadContentBlocks();
              }, 200);
            }
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loading = false;
          this.stabHidden = false;
          this.detectChanges();
        },
      });
  }

  public setStabVisible(pane: IContentPane, hasContent: boolean) {
    if (hasContent) {
      this.stabHidden = true;
      this.remainUnloaded = -1;
    } else {
      if (this.remainUnloaded > 0) {
        this.remainUnloaded -= 1;
      }
    }
    if (this.remainUnloaded === 0) {
      this.stabHidden = false;
    }
  }

  protected setupMeta() {
    this.metaService.setTitle(['schools.meta-title']);
  }
}
